/**
 * LogBridgeServiceModule is a service module that provides logging capabilities
 * through a bridge or the default console. It also emits signals for error handling.
 */
class LogBridgeServiceModule {
    /**
     * Constructor for LogBridgeServiceModule.
     * @param {Object} $signals - The signals service for emitting events.
     * @param {Object} $timeout - The AngularJS timeout service.
     */
    constructor($signals, $timeout) {
        this.$signals = $signals;
        this.$timeout = $timeout;
    }

    /**
     * Checks if the bridge is available.
     * @returns {boolean} - True if the bridge is available, false otherwise.
     */
    isBridgeAvailable() {
        return typeof window.jsBridgeLog !== 'undefined';
    }

    /**
     * Gets the bridge context for logging.
     * @returns {Object} - The logger object, either the bridge or the console.
     */
    getBridgeContext() {
        const logger = window.jsBridgeLog || console;
        return logger;
    }

    /**
     * Logs data through the bridge or console.
     * @param {string} logLevel - The level of the log (e.g., 'info', 'warn', 'error', 'dev').
     * @param {Array} logData - The data to be logged.
     */
    bridgeLog(logLevel, logData) {
        const logger = this.getBridgeContext();
        let logline = logData;
        let parts = [];
        const caller = fetchCaller(logline);

        // Add the caller identifier if present
        if (caller !== undefined) {
            logline = [caller].concat(logline.slice(1));
        }

        // Adjust log level if the bridge is not available and the level is 'dev'
        if (!this.isBridgeAvailable() && logLevel === 'dev') {
            logLevel = 'debug';
        }

        // Log the data if the logger supports the log level
        if (typeof logger[logLevel] === 'function') {
            logger[logLevel](logline.join(' '));
        }

        // Emit an error handler signal after a timeout
        if (logLevel === 'error') {
            this.$timeout(() => {
                this.$signals.emit('ErrorHandler', logLevel, logline);
            }, 100);
        }
    }

    /**
     * Logs information.
     */
    info() {
        this.bridgeLog('info', Array.from(arguments));
    }

    /**
     * Logs a warning.
     */
    warn() {
        this.bridgeLog('warn', Array.from(arguments));
    }

    /**
     * Logs an error.
     */
    error() {
        this.bridgeLog('error', Array.from(arguments));
    }

    /**
     * Logs development information.
     */
    dev() {
        this.bridgeLog('dev', Array.from(arguments));
    }

    /**
     * Helper method for tracing function entry.
     */
    logEnter() {
        /*const caller = arguments[0];
        const functionName = arguments[1];
        const args = Array.from(arguments).slice(2);
        const passings = [].concat(args);
        */  
        //this.dev(caller, ' --> ', functionName, args);
    }

    /**
     * Helper method for tracing function exit.
     * @param {string} caller - The caller identifier.
     * @param {string} methodName - The method name.
     */
    logExit(caller, methodName) {
        //this.dev(caller, ' <-- ', methodName);
    }
}

// Private Helpers

/**
 * Fetches the caller identifier from the logline.
 * @param {Array} logline - The logline containing the caller information.
 * @returns {string|undefined} - The caller identifier or undefined if not found.
 */
function fetchCaller(logline) {
    let caller;

    // Search for object identifier in logData
    if (logline && typeof logline === 'object') {
        if (logline.length > 0 && typeof logline[0] === 'object') {
            if (typeof logline[0]._logIdentifier === 'string') {
                caller = logline[0]._logIdentifier;
            }
        }
    }

    return typeof caller !== 'undefined' ? `[${caller}]` : caller;
}

// Add explicit dependency injection
LogBridgeServiceModule.$inject = ['$signals', '$timeout'];

export default LogBridgeServiceModule;